import { Inject, Injectable } from "@angular/core";
import { createEffect } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import {
	AllowedAction,
	AllowedActions,
	CORE_ROUTE,
	CoreState,
	CurrentAction,
	CustomerComplianceConfig,
	CustomerComplianceService,
	CustomerManagementSelector,
	DisallowedActionCodes,
	KycMessageService,
	KycState,
} from "@obg/ngx.core";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { DataBusFacade, EventBusFacade } from "remote-module-context";
import { combineLatest } from "rxjs";
import { switchMap, take, tap, withLatestFrom } from "rxjs/operators";

import { T_SHELL_DATA_BUS_FACADE, T_SHELL_EVENT_BUS_FACADE } from "../injection";
import { observeEvent } from "../util/observe-event";

@Injectable({
	providedIn: "root",
})
export class KycEffects {
	// We have to really jump through some hoops to get this to work due to the current miserable state of KYC.
	// We have created a ticket that will clean data:kycAllowedActions and event:handleKycBlockedAction once the KYC service and guard is
	// refactored. We have also escalated the need for this. Our refactoring ticket: https://edgealpha.corpsson.com/browse/AC-4877
	kycAllowedActions$ = createEffect(
		() =>
			combineLatest(
				Object.values(AllowedActions).map((actionType: AllowedActions) =>
					this.customerComplianceService.kycStatusCheck$(actionType)
				)
			).pipe(
				withLatestFrom(this.store.select(CustomerManagementSelector.getAllowedAction)),
				tap(
					([allActionStatus, customerAllowedActions]: [
						allActionStatus: CurrentAction[],
						customerAllowedActions: AllowedAction,
					]) => {
						this.dataBus.dispatch({
							key: "kycAllowedActions",
							value: allActionStatus.reduce((acc, actionStatus: CurrentAction) => {
								const action = customerAllowedActions?.[actionStatus.action];

								return {
									...acc,
									[actionStatus.action]:
										actionStatus.isAllowed &&
										!(
											this.customerComplianceConfig.getIsCustomerAssessmentsEnabled() &&
											action?.disallowedReasons?.includes(
												DisallowedActionCodes.M_CURRENTCUSTOMER_ASSESSMENTREQUIRED
											) &&
											!action?.isAllowed
										),
								};
							}, {}),
						});
					}
				)
			),
		{ dispatch: false }
	);

	handleBlockedAction$ = createEffect(
		() =>
			observeEvent<{ actionName: keyof AllowedAction }>(this.eventBus, "handleKycBlockedAction").pipe(
				switchMap(({ actionName }) =>
					combineLatest([
						this.customerComplianceService.kycStatusCheck$(actionName),
						this.store.select(CustomerManagementSelector.getAllowedAction),
						this.store.select(RouteSelector.getCurrent),
					]).pipe(
						take(1),
						tap(([currentAction, customerAllowedActions, currentRoute]) => {
							const action = customerAllowedActions?.[actionName];
							if (
								this.customerComplianceConfig.getIsCustomerAssessmentsEnabled() &&
								action?.disallowedReasons?.includes(
									DisallowedActionCodes.M_CURRENTCUSTOMER_ASSESSMENTREQUIRED
								) &&
								!action?.isAllowed
							) {
								return; // do not show kyc message because a mandatory assessment is required
							}

							const isDutchAndNotAccepted =
								currentAction.complianceType === "Dutch" &&
								currentAction.currentStatus !== KycState.Accepted;
							const isAccessingBlockedResource =
								!!currentRoute?.queryParams?.returnUrl && currentRoute?.name === CORE_ROUTE.auth.login;
							if (!currentRoute || (isAccessingBlockedResource && !isDutchAndNotAccepted)) {
								this.store.dispatch(RouteAction.navigate(CORE_ROUTE.home));
								return;
							}

							if (isAccessingBlockedResource && isDutchAndNotAccepted) {
								return;
							}

							this.kycMessageService.showMessage(currentAction);
						})
					)
				)
			),
		{ dispatch: false }
	);

	constructor(
		@Inject(T_SHELL_DATA_BUS_FACADE) private dataBus: DataBusFacade,
		@Inject(T_SHELL_EVENT_BUS_FACADE) private eventBus: EventBusFacade,
		private store: Store<CoreState>,
		private customerComplianceService: CustomerComplianceService,
		private customerComplianceConfig: CustomerComplianceConfig,
		private kycMessageService: KycMessageService
	) {}
}
